import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../utils/api";
import { Validation } from "../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import useRouteParam from "../../../../hooks/useRouteParam";
import { Group } from "../../../../@types/group.types";
import { useEmployerRoute } from "../../../../hooks";

export type BuyUpEligibilityTypes = {
	group_number: number | null;
	employee_SSN: string;
	buy_up_eligibility: boolean | null;
};

const chipMessage = "This functionality will update the Buy Up Eligibility for Employee of specific group.";

const BuyUpEligibilityUpdation = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<BuyUpEligibilityTypes>({
		group_number: null,
		employee_SSN: "",
		buy_up_eligibility: null
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, employee_SSN, buy_up_eligibility } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (employee_SSN.length === 0) {
			_validation.current.reportFieldDetails["employee_SSN"] = "Employee SSN is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (buy_up_eligibility === null) {
			_validation.current.reportFieldDetails["buy_up_eligibility"] = "Buy up eligibility is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			if (name === "buy_up_eligibility") {
				if (value === "Yes")
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							buy_up_eligibility: true
						})
					);
				else
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							buy_up_eligibility: false
						})
					);
			} else {
				setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
			}
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Buy Up Eligibility Updation");
					const { group_number, employee_SSN, buy_up_eligibility } = reportFieldDetails;

					const payload: any = {
						group_number,
						employee_SSN: employee_SSN.replaceAll("-", ""),
						buy_up_eligibility
					};

					// console.log("Buy Up Eligibility Updation Payload", payload);
					const response: any = await trackPromise(api.memberInfoUpdate.buyUpEligibilityUpdate(payload));

					if (response) {
						// console.log("Response", response);
						alert("Buy Up Eligibility Updation Successful");
						window.location.reload();

						// setTimeout(() => {
						// 	setSnackbarAPICallProps(
						// 		Object.assign({}, snackbarAPICallProps, {
						// 			open: true,
						// 			message: "Buy Up Eligibility Updation Successful!",
						// 			severity: "success"
						// 		})
						// 	);
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Buy Up Eligibility Updation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.buy_up_eligibility);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Buy Up Eligibility Updation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Employee SSN",
							name: "employee_SSN",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Employee SSN",
							value: reportFieldDetails.employee_SSN,
							type: "textfield"
						},
						{
							label: "Buy Up Eligibility",
							name: "buy_up_eligibility",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								reportFieldDetails.buy_up_eligibility === true
									? "Yes"
									: reportFieldDetails.buy_up_eligibility === false
									? "No"
									: "",
							type: "select",
							options: ["Yes", "No"]
						}
					]
				}
			)
		);
	}, [assignedGroups, reportFieldDetails, handleChange, isEmployerRoute, queryGroupNumber, selectedGroup?.name]);

	// console.log("reportFieldDetails", reportFieldDetails);
	// console.log("Buy Up Eligibility Validation", validation);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default BuyUpEligibilityUpdation;
